import { useState } from "react";
import { useGenerateSuggestionsLazyQuery, RequestedFeature, PlaydateInviteSuggestion, ScheduleOptionType, PlaydateType, PlaydateGuestType } from "../../../gql/types"; // adjust path
import { useToastContext } from "contexts/ToastContext";

type GenerateCreateInviteSuggestionOptions = {
  userMessage?: string;
  date?: string;               // ISO string, e.g., "2025-05-01"
  connectionIDs?: string[];     // NEW: array of connection IDs
  eventID?: string;
  onCompletion: (success: boolean, suggestions?: PlaydateInviteSuggestion[]) => void;
};

export function useGenerateCreateInviteSuggestions() {
  const [triggerGenerateSuggestions] = useGenerateSuggestionsLazyQuery();
  const [loading, setLoading] = useState(false);
  const { pushToastEvent } = useToastContext();

  const generateCreateInviteSuggestions = async ({
    userMessage,
    date,
    connectionIDs,
    eventID,
    onCompletion,
  }: GenerateCreateInviteSuggestionOptions) => {
    setLoading(true);

    try {
      const guestIDs = connectionIDs && connectionIDs.length > 0
        ? [...connectionIDs]
        : undefined;

      const { data } = await triggerGenerateSuggestions({
        variables: {
          input: {
            requestedFeature: RequestedFeature.CreatePlaydate,
            context: {
              playdateInviteContext: {
                eventID: eventID,
              }
            },
            parameters: {
              userMessage,
              playdateInviteInput: {
                date: date,
                connectionIDs: guestIDs,
              },
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const playdateInviteSuggestions =
        data?.generateSuggestions?.playdateInviteSuggestions || [];

      if (playdateInviteSuggestions.length === 0) {
        throw new Error("No suggestions returned");
      }

      pushToastEvent({
        eventType: "success",
        message: "Generated playdate invite suggestions!",
      });

      onCompletion(true, playdateInviteSuggestions);
    } catch (err) {
      console.error(err);
      pushToastEvent({
        eventType: "error",
        message: "Failed to generate suggestions.",
      });
      onCompletion(false);
    } finally {
      setLoading(false);
    }
  };

  return { generateCreateInviteSuggestions, loading };
}
